/**
 * Buybox: Seeds — bundle-builder seed selector.
 * The card row is a Swiper carousel (slidesPerView: auto). The next-arrow
 * advances one slide and hides itself at the end.
 * "See all varieties" opens the shared <nss-drawer> (opened by nss-drawer.js via
 * the [nss-drawer-open] trigger); inside it, category chips filter the list and
 * each variety is an accordion row that expands to its big image + description.
 */
class NSSBuyboxSeeds extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector(`.js-nss-buybox-seeds`);
    this.next = this.querySelector(`[nss-seeds-next]`);
    this.prev = this.querySelector(`[nss-seeds-prev]`);

    this.initSwiper();
    this.initQuantitySwiper();

    if (this.next) {
      this.next.addEventListener(`click`, () => {
        if (this.swiper) this.swiper.slideNext();
      });
    }

    if (this.prev) {
      this.prev.addEventListener(`click`, () => {
        if (this.swiper) this.swiper.slidePrev();
      });
    }

    this.addEventListener(`click`, this.handleClick.bind(this));

    this.varietiesDrawer = this.querySelector(`nss-drawer.nss-buybox__seeds-drawer`);
    if (this.varietiesDrawer) {
      this.varietiesDrawer.addEventListener(`click`, this.handleVarietyClick.bind(this));
    }

    this.addEventListener(`change`, this.handleChange.bind(this));
    this.syncQuantitySize();
    this.updateAtcButton();
    this.syncStickyPrice();
    this.syncStickyAtc();
  }

  handleChange(e) {
    const qty = e.target.closest(`[nss-qty-option]`);
    if (qty && qty.checked) {
      this.setAttribute(`data-seeds-size`, qty.getAttribute(`data-seeds-size`) || `8oz`);
      this.syncPrices(qty);
      this.updateAtcButton();
      this.syncStickyPrice();
      this.syncStickyAtc();
      return;
    }
    if (e.target.closest(`[nss-bundle-frequency-radio]`)) {
      this.updateAtcButton();
      this.syncStickyPrice();
      this.syncStickyAtc();
    }
  }

  // Sticky ATC bars portal themselves to <body>, so the section scope
  // misses them; match portaled bars by their nss-parent-section-id.
  stickyEls(hook) {
    const scope = this.closest(`[id^="shopify-section"]`) || document;
    const sid = scope.id ? scope.id.replace(`shopify-section-`, ``) : null;
    const els = Array.from(
      scope.querySelectorAll(`nss-buybox-sticky-atc-variant ${hook}`)
    );
    if (sid) {
      els.push(
        ...document.querySelectorAll(
          `body > nss-buybox-sticky-atc-variant[nss-parent-section-id="${sid}"] ${hook}`
        )
      );
    }
    return els;
  }

  // Mirror the bundle ATC state onto the sticky bar button: when the bundle
  // button is disabled (stock-status metafield or an unavailable component for
  // the selected quantity / purchase option) the sticky button shows the same
  // text, disabled, in the sold-out style. Re-enabled = original label restored.
  syncStickyAtc() {
    const btn = this.querySelector(`[nss-bundle-add-to-cart]`);
    if (!btn) return;
    const disabled = btn.hasAttribute(`disabled`);
    let text = ``;
    if (disabled) {
      if (btn.hasAttribute(`nss-bundle-stock-disabled`)) {
        text = btn.textContent.trim();
      } else {
        const sold = this.querySelector(`[nss-bundle-atc-soldout]`);
        text = sold ? sold.textContent.trim() : `Sold out`;
      }
    }
    this.stickyEls(`[nss-sticky-atc]`).forEach((el) => {
      if (disabled) {
        if (!el.hasAttribute(`data-nss-original-html`)) {
          el.setAttribute(`data-nss-original-html`, el.innerHTML);
        }
        el.classList.add(`nss-buybox-sticky-atc__atc--soldout`);
        el.setAttribute(`disabled`, ``);
        el.textContent = text;
      } else if (el.hasAttribute(`data-nss-original-html`)) {
        el.innerHTML = el.getAttribute(`data-nss-original-html`);
        el.removeAttribute(`data-nss-original-html`);
        el.classList.remove(`nss-buybox-sticky-atc__atc--soldout`);
        el.removeAttribute(`disabled`);
      }
    });
  }

  syncStickyPrice() {
    const qty = this.querySelector(`[nss-qty-option]:checked`);
    if (!qty) return;
    const subscribe = this.isSubscribe();
    const priceText = subscribe
      ? qty.getAttribute(`data-sub-today`)
      : qty.getAttribute(`data-otp-today`);
    const compareText = qty.getAttribute(`data-compare`);
    const toNum = (t) => {
      if (!t) return null;
      const n = parseFloat(String(t).replace(/[^0-9.]/g, ``));
      return isNaN(n) ? null : n;
    };
    const stickyEls = (hook) => this.stickyEls(hook);
    stickyEls(`[nss-selected-frequency-price]`)
      .forEach((el) => {
        if (priceText) el.textContent = priceText;
      });
    stickyEls(`[nss-selected-frequency-compare-at-price]`)
      .forEach((el) => {
        const c = toNum(compareText);
        const pr = toNum(priceText);
        if (compareText && c != null && pr != null && c > pr) {
          el.textContent = compareText;
          el.style.display = ``;
        } else {
          el.style.display = `none`;
        }
      });
    stickyEls(`[nss-sticky-sub-only]`)
      .forEach((el) => {
        el.hidden = !subscribe;
      });
  }

  isSubscribe() {
    const f = this.querySelector(`[nss-bundle-frequency-radio]:checked`);
    return f ? f.value !== `` : true;
  }

  isSoldOut(qty) {
    const attr = this.isSubscribe() ? `data-sub-available` : `data-otp-available`;
    return qty.getAttribute(attr) === `false`;
  }

  updateAtcButton() {
    const qty = this.querySelector(`[nss-qty-option]:checked`);
    if (!qty) return;

    const priceEl = this.querySelector(`[nss-bundle-atc-price]`);
    if (priceEl) {
      const price = this.isSubscribe()
        ? qty.getAttribute(`data-sub-today`)
        : qty.getAttribute(`data-otp-today`);
      if (price) priceEl.textContent = price;
    }

    const btn = this.querySelector(`[nss-bundle-add-to-cart]`);
    const dflt = this.querySelector(`[nss-bundle-atc-default]`);
    const sold = this.querySelector(`[nss-bundle-atc-soldout]`);
    const soldOut = this.isSoldOut(qty);
    if (btn) {
      if (soldOut || btn.hasAttribute(`nss-bundle-stock-disabled`)) btn.setAttribute(`disabled`, ``);
      else btn.removeAttribute(`disabled`);
    }
    if (dflt) dflt.hidden = soldOut;
    if (sold) sold.hidden = !soldOut;

    const bagsEl = this.querySelector(`[nss-bundle-otp-bags]`);
    if (bagsEl) {
      const n = Number(qty.getAttribute(`data-seed-qty`)) || 1;
      bagsEl.textContent = `${n} seed bag${n > 1 ? `s` : ``}`;
    }
  }

  addToCart(btn) {
    const qty = this.querySelector(`[nss-qty-option]:checked`);
    if (!qty) return;
    if (this.isSoldOut(qty)) return;

    const subscribe = this.isSubscribe();
    const sprouterVariant = subscribe
      ? qty.getAttribute(`data-sub-sprouter-variant`) || qty.getAttribute(`data-sprouter-variant`)
      : qty.getAttribute(`data-sprouter-variant`);
    const sprouterQty = Number(qty.getAttribute(`data-sprouter-qty`)) || 1;
    const seedVariant = qty.getAttribute(`data-seed-variant`);
    const seedQty = Number(qty.getAttribute(`data-seed-qty`)) || 1;

    // Unique token shared by every line in this bundle so they can be grouped
    // in the cart. Regular products carry `n-<uid>`; the sprouter carries
    // `n-sprouters-<uid>` (same uid, distinguished by the `sprouters-` infix).
    const bundleUid = `${Date.now().toString(36)}${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    // Resolve the seed's subscription plan up front so the sprouter tag can
    // depend on whether the seed is actually a subscription.
    let seedPlan = ``;
    if (subscribe) {
      const sel = this.querySelector(`[nss-bundle-plan]`);
      const radio = this.querySelector(`[nss-bundle-frequency-radio]:checked`);
      seedPlan = (sel && sel.value) || (radio && radio.value) || ``;
    }
    const seedHasSubscription = subscribe && !!seedPlan;

    // Bundle metadata consumed by the Rebuy Smart Cart template:
    // `_bundle_qty` — per-bundle unit quantity of the line (e.g. seed 2 for
    // 2×8oz) so the cart's bundle stepper scales every line in ratio.
    // `_bundle_title` — labels the bundle group box in the cart.
    const bundleTitle = this.getAttribute(`data-bundle-title`) || ``;

    const items = [];
    if (sprouterVariant) {
      // Only flag the sprouter as a `sprouters-` line when the seed is a
      // subscription; otherwise it groups like any regular bundle line.
      const sprouterTag = seedHasSubscription
        ? `n-sprouters-${bundleUid}`
        : `n-${bundleUid}`;
      const sprouterProps = {
        _is_bundle: sprouterTag,
        _is_bundle_group: `n-${bundleUid}`,
        _bundle_qty: String(sprouterQty),
      };
      if (bundleTitle) sprouterProps._bundle_title = bundleTitle;
      items.push({
        id: Number(sprouterVariant),
        quantity: sprouterQty,
        properties: sprouterProps,
      });
    }
    if (seedVariant) {
      const seedProps = {
        _is_bundle: `n-${bundleUid}`,
        _is_bundle_group: `n-${bundleUid}`,
        _bundle_qty: String(seedQty),
      };
      if (bundleTitle) seedProps._bundle_title = bundleTitle;
      const seedItem = {
        id: Number(seedVariant),
        quantity: seedQty,
        properties: seedProps,
      };
      if (seedPlan) seedItem.selling_plan = Number(seedPlan);
      items.push(seedItem);
    }

    if (!items.length) return;

    if (btn) btn.setAttribute(`disabled`, ``);
    fetch(window.Shopify.routes.root + `cart/add.js`, {
      method: `POST`,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    })
      .then(() => {
        if (typeof window.NSS_REFRESH_AND_OPEN_CART === `function`) {
          window.NSS_REFRESH_AND_OPEN_CART();
        } else {
          window.location.href = window.Shopify.routes.root + `cart`;
        }
      })
      .catch((err) => console.error(`[NSS][Bundle ATC]`, err))
      .finally(() => {
        if (btn && !btn.hasAttribute(`nss-bundle-stock-disabled`)) btn.removeAttribute(`disabled`);
      });
  }

  syncQuantitySize() {
    const checked = this.querySelector(`[nss-qty-option]:checked`);
    if (checked) {
      this.setAttribute(`data-seeds-size`, checked.getAttribute(`data-seeds-size`) || `8oz`);
      this.syncPrices(checked);
    }
  }

  syncPrices(qty) {
    if (!qty) return;
    const set = (hook, val) => {
      const el = this.querySelector(`[${hook}]`);
      if (el && val) el.textContent = val;
    };
    set(`nss-bundle-price-sub`, qty.getAttribute(`data-sub-today`));
    set(`nss-bundle-price-otp`, qty.getAttribute(`data-otp-today`));
    set(`nss-bundle-price-compare`, qty.getAttribute(`data-compare`));
    set(`nss-bundle-price-otp-compare`, qty.getAttribute(`data-compare`));
    set(`nss-bundle-price-refill`, qty.getAttribute(`data-refill`));
  }

  initSwiper() {
    if (!this.carousel || typeof Swiper === `undefined`) {
      this.syncArrow();
      return;
    }

    this.swiper = new Swiper(this.carousel, {
      slidesPerView: `auto`,
      spaceBetween: 8,
      watchOverflow: true,
      // Slides are radio options inside a named fieldset; Swiper's "1 / 10" group roles only add noise.
      a11y: { enabled: false },
      scrollbar: {
        el: this.querySelector(`.nss-buybox__seeds-scrollbar`),
        draggable: true,
      },
      on: {
        init: () => this.syncArrow(),
        slideChange: () => this.syncArrow(),
        resize: () => this.syncArrow(),
        reachEnd: () => this.syncArrow(),
        reachBeginning: () => this.syncArrow(),
        fromEdge: () => this.syncArrow(),
      },
    });
  }

  initQuantitySwiper() {
    this.qtyCarousel = this.querySelector(`.js-nss-buybox-quantity`);
    this.qtyNext = this.querySelector(`[nss-quantity-next]`);
    this.qtyPrev = this.querySelector(`[nss-quantity-prev]`);
    if (!this.qtyCarousel || typeof Swiper === `undefined`) {
      this.syncQuantityArrow();
      return;
    }

    if (this.qtyNext) {
      this.qtyNext.addEventListener(`click`, () => {
        if (this.qtySwiper) this.qtySwiper.slideNext();
      });
    }
    if (this.qtyPrev) {
      this.qtyPrev.addEventListener(`click`, () => {
        if (this.qtySwiper) this.qtySwiper.slidePrev();
      });
    }

    this.qtySwiper = new Swiper(this.qtyCarousel, {
      slidesPerView: `auto`,
      spaceBetween: 8,
      watchOverflow: true,
      a11y: { enabled: false },
      observer: true,
      observeParents: true,
      scrollbar: {
        el: this.querySelector(`.nss-buybox__quantity-scrollbar`),
        draggable: true,
      },
      on: {
        init: () => this.syncQuantityArrow(),
        slideChange: () => this.syncQuantityArrow(),
        resize: () => this.syncQuantityArrow(),
        reachEnd: () => this.syncQuantityArrow(),
        reachBeginning: () => this.syncQuantityArrow(),
        fromEdge: () => this.syncQuantityArrow(),
        imagesReady: () => {
          this.qtySwiper.update();
          this.syncQuantityArrow();
        },
      },
    });

    const qtyRefresh = () => {
      if (!this.qtySwiper) return;
      this.qtySwiper.update();
      if (this.qtySwiper.scrollbar && this.qtySwiper.scrollbar.updateSize) {
        this.qtySwiper.scrollbar.updateSize();
      }
      this.syncQuantityArrow();
    };
    const qtyRefreshAll = () => {
      qtyRefresh();
      setTimeout(qtyRefresh, 300);
    };
    if (document.readyState === `complete`) {
      requestAnimationFrame(qtyRefreshAll);
    } else {
      window.addEventListener(`load`, qtyRefreshAll, { once: true });
    }
  }

  syncQuantityArrow() {
    const locked = this.qtySwiper ? this.qtySwiper.isLocked : true;
    const showNext = !!this.qtySwiper && !locked && !this.qtySwiper.isEnd;
    const showPrev = !!this.qtySwiper && !locked && !this.qtySwiper.isBeginning;
    if (this.qtyNext) this.qtyNext.style.display = showNext ? `flex` : `none`;
    if (this.qtyPrev) this.qtyPrev.style.display = showPrev ? `flex` : `none`;
  }

  syncArrow() {
    let showNext = true;
    let showPrev = false;
    if (this.swiper) {
      showNext = !this.swiper.isLocked && !this.swiper.isEnd;
      showPrev = !this.swiper.isLocked && !this.swiper.isBeginning;
    } else if (this.carousel) {
      showNext = this.carousel.scrollWidth > this.carousel.clientWidth + 2;
      showPrev = false;
    }
    if (this.next) this.next.style.display = showNext ? `` : `none`;
    if (this.prev) this.prev.style.display = showPrev ? `flex` : `none`;
  }

  handleClick(e) {
    const atc = e.target.closest(`[nss-bundle-add-to-cart]`);
    if (atc) {
      e.preventDefault();
      this.addToCart(atc);
      return;
    }
  }

  handleVarietyClick(e) {
    const chip = e.target.closest(`[nss-variety-filter]`);
    if (chip) {
      this.filterVarieties(chip);
      return;
    }

    const toggle = e.target.closest(`[nss-variety-toggle]`);
    if (toggle) {
      this.toggleVariety(toggle);
      return;
    }
  }

  filterVarieties(chip) {
    const category = chip.getAttribute(`data-category`) || `all`;
    const root = this.varietiesDrawer || this;

    root.querySelectorAll(`[nss-variety-filter]`).forEach((el) => {
      el.classList.toggle(`nss-active`, el === chip);
    });

    root.querySelectorAll(`.nss-buybox__varieties-item`).forEach((item) => {
      const cats = (item.getAttribute(`data-categories`) || ``).split(`|`);
      const show = category === `all` || cats.includes(category);
      item.hidden = !show;
    });
  }

  toggleVariety(toggle) {
    const item = toggle.closest(`.nss-buybox__variety`);
    if (!item) return;
    const willOpen = !item.classList.contains(`nss-active`);
    const root = this.varietiesDrawer || this;

    root.querySelectorAll(`.nss-buybox__variety.nss-active`).forEach((openItem) => {
      if (openItem === item) return;
      openItem.classList.remove(`nss-active`);
      const t = openItem.querySelector(`[nss-variety-toggle]`);
      if (t) t.setAttribute(`aria-expanded`, `false`);
    });

    item.classList.toggle(`nss-active`, willOpen);
    toggle.setAttribute(`aria-expanded`, willOpen ? `true` : `false`);
  }
}

if (!customElements.get(`nss-buybox-seeds`)) {
  customElements.define(`nss-buybox-seeds`, NSSBuyboxSeeds);
}
